import type { ReactNode } from 'react';
import { CircleAlert, CircleCheck, Info, TriangleAlert, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * A bordered message in a status tone. Drawn like the HyperTek prototype's `components/states/list-error-banner.tsx`
 * — a thin border in the tone, the tone's pale tint, `p-3`, body-size text — with an icon before the words. The
 * words carry the meaning; the tint and the icon only say which kind of message it is. Error and success read
 * in the tone's own dark colour; a warning or a piece of advice keeps the body colour for the words (they are
 * often a few sentences long) and puts the tone on the icon.
 *
 * `role` is the caller's, because only the caller knows what the message is: `alert` for a refusal, `status`
 * for a quiet confirmation, nothing for advice that was on the screen from the start.
 */

type Tone = 'error' | 'warning' | 'success' | 'info';

/** The tone's frame, shared with the product's other status panels (the error summary, the conflict panel). */
export const NOTICE_TONE: Record<Tone, string> = {
  error: 'border-[hsl(var(--h-error)/0.45)] bg-[hsl(var(--h-error-soft))] text-[hsl(var(--h-error-fg))]',
  warning: 'border-[hsl(var(--h-warning)/0.55)] bg-[hsl(var(--h-warning-soft))] text-[hsl(var(--h-card-foreground))]',
  success: 'border-[hsl(var(--h-success)/0.45)] bg-[hsl(var(--h-success-soft))] text-[hsl(var(--h-success-fg))]',
  info: 'border-[hsl(var(--h-info)/0.45)] bg-[hsl(var(--h-info-soft))] text-[hsl(var(--h-card-foreground))]',
};

/** The prototype banner's box: every status panel in the product is this shape in its tone. */
export const NOTICE_BOX = 'rounded-[var(--h-radius-md)] border p-3 text-sm';

const ICON: Record<Tone, { icon: string; Icon: LucideIcon }> = {
  error: { icon: '', Icon: CircleAlert },
  warning: { icon: 'text-[hsl(var(--h-warning-fg))]', Icon: TriangleAlert },
  success: { icon: '', Icon: CircleCheck },
  info: { icon: 'text-[hsl(var(--h-info-fg))]', Icon: Info },
};

export function Notice({ tone, role, icon, className, children }: {
  tone: Tone;
  role?: 'alert' | 'status';
  /** Replaces the tone's own icon when a more specific one says the same thing better (an eye for read-only). */
  icon?: LucideIcon;
  className?: string;
  children: ReactNode;
}) {
  const { icon: iconTone, Icon: ToneIcon } = ICON[tone];
  const Icon = icon ?? ToneIcon;
  return (
    <div data-slot="notice" role={role} className={cn('flex gap-2.5', NOTICE_BOX, NOTICE_TONE[tone], className)}>
      <Icon className={cn('mt-0.5 size-4 shrink-0', iconTone)} aria-hidden="true" />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
